"use client";

import { icon } from "@/lib/icons/icon";
import {
  CheckmarkCircle02Icon as g_CheckmarkCircle02Icon,
  InfoIcon as g_InfoIcon,
  Loading03Icon as g_Loading03Icon,
  OctagonXIcon as g_OctagonXIcon,
  TriangleAlertIcon as g_TriangleAlertIcon,
} from "@hugeicons/core-free-icons";
import { useTheme } from "next-themes";
import { useIsMobile } from "@/hooks/use-mobile";
import { Toaster as Sonner, type ToasterProps } from "sonner";

/* Straight from the icon package rather than lib/icons: the toaster is on
   every page, and the barrel would put every glyph in the app there too. */
const CircleCheckIcon = icon(g_CheckmarkCircle02Icon);
const InfoIcon = icon(g_InfoIcon);
const Loader2Icon = icon(g_Loading03Icon);
const OctagonXIcon = icon(g_OctagonXIcon);
const TriangleAlertIcon = icon(g_TriangleAlertIcon);

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  /* A phone's toasts come down from the top, the way its own notices do, instead of landing on
     its bottom bar and the floating action; a desk keeps sonner's corner. */
  const phone = useIsMobile();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position={phone ? "top-center" : props.position}
      /* Six seconds and a close button, not sonner's four and none: a two-line
         error is not read in four seconds by somebody mid-task, and a toast
         that has been read should be dismissible. */
      duration={6000}
      closeButton
      /* A toast wears the tone of what it says, in the app's own status
         triples: a green edge on "Saved", a red one on a failure, rather
         than four identical white slips. */
      richColors
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          "--success-bg": "var(--ok-soft)",
          "--success-text": "var(--ok)",
          "--success-border": "var(--ok-border)",
          "--error-bg": "var(--crit-soft)",
          "--error-text": "var(--crit)",
          "--error-border": "var(--crit-border)",
          "--warning-bg": "var(--warn-soft)",
          "--warning-text": "var(--warn)",
          "--warning-border": "var(--warn-border)",
          "--info-bg": "var(--info-soft)",
          "--info-text": "var(--info)",
          "--info-border": "var(--info-border)",
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
